import { CSSProperties, ReactNode } from "react"
import { isUndefinedOrNull } from "../utils/utils"
import { FormGroup } from "./FormGroup"
import { classes } from "./react"

interface cssStyleObject {
    width?: string
    paddingLeft?: string
    paddingTop?: string
    paddingBottom?: string
    border?: string
    outline?: string
    align?: string
}

/**
 * Props for NumericInput (see below)
 */
interface NumericInputProps {
    /** Current value */
    value: number | undefined

    /** Function called to change the value */
    setValue?(value: number): void

    /**
     * If true, the input will be flagged as required
     * (there is no local validation, so this would only have an impact if
     * using a form with a "post" method)
     **/
    required?: boolean

    /**
     * Minimum value
     * (not used for validation, this is just passed to the input to
     * generate the error tooltip)
     */
    min?: number

    /**
     * Maximum value (not used for validation, just passed to the input to
     * generate the error tooltip)
     */
    max?: number

    /**
     * Step (increment between two values)
     */
    step?: number

    /** Default value - currently only used as a shortcut to set the
     * placeholder using a number */
    defaultValue?: number

    /** Placeholder - only used if "defaultValue" is not set */
    placeholder?: string

    readOnly?: boolean

    disabled?: boolean

    /**
     * Additional CSS classes
     */
    className?: string

    cssStyle?: cssStyleObject

    /**
     * If true, an approximate with will be applied to the style,
     * based on the number of characters in the text (using the "ch" unit)
     * This is mainly a legacy of a specific Omnia feature where this
     * component was first created
     * (i.e., the "Production" module)
     */
    setWidthFromLength?: boolean

    /**
     * Indicates if the "spinners" (up/down) arrows should be displayed
     * Defaults to true
     * If false, will apply a specific class to hide them
     **/
    spinners?: boolean

    /** If true, setValue will only be called when the input loses focus */
    commitOnBlur?: boolean

    /**
     * If format is set to 'displayLeadingZero' add 0 before the decimal place
     * This will likely be extended later to support other formatting options,
     * which is
     */
    format?: "displayLeadingZero"

    /**An action that happens when the input box is clicked on/into */
    onClickAction?: () => void
}

/**
 * Input for numeric values
 * NB Setting setWidthFromLength to true will override the width
 * set in cssStyle
 */
export function NumericInput({
    value,
    setValue,
    min,
    max,
    step,
    defaultValue,
    placeholder,
    className,
    cssStyle,
    required,
    readOnly,
    disabled,
    spinners,
    setWidthFromLength,
    commitOnBlur,
    format,
    onClickAction,
}: NumericInputProps) {
    function isNumeric(t: any) {
        const n = parseFloat(t)
        return isNaN(n) ? false : n
    }

    // "valueText" is the text reprentation of "value"
    const valueText = typeof value === "number" ? String(value) : ""

    const ref = React.useRef<HTMLInputElement>()

    /**
     * The state for this component is a [string, number] tuple
     * The string component is called "text", and bound to the input - this
     * is what is displayed in the input, and this changes when users are
     * typing.
     *
     * The number component is called "textValue", and is the numeric
     * representation of this text.
     *
     * It can be undefined if the text cannot currently be parsed as a number
     * It is mainly used to decide when to reset the text from the value,
     * through the "useEffect" hook below.
     *
     * As long as "value" (the value the the props) and "textValue" (the value
     * obtained by converting the text to a number) have not changed, then
     * nothing happens. Otherwise, the text will be reset to represent "value".
     */
    const [[text, textValue], setText] = React.useState([valueText, value])

    // This effect is used to tranform the numeric "value" passed through the
    // props to the corresponding text representation displayed in the input
    React.useEffect(() => {
        if (value != textValue) {
            setText([valueText, value])
        }
    }, [value, textValue])

    /**
     * This is called to change the text and corresponding numeric value
     *
     * This can be called either "onChange", or "onBlur"
     *
     * The "commit" parameter indicates if this is called while
     * typing ("onChange") or to commit the changes ("onBlur"). This is
     * currently unused, but will likely be handy later on if we add other
     * formats that cannot be easily while typing.
     *
     * @param t Text
     * @param commit If true, this is called on commit
     */
    const set = (t: string, commit: boolean) => {
        const n = isNumeric(t)
        let nextValue: number = undefined

        if (n !== false) {
            /** If a specific format is requested, it is applied here,
             * by converting the numeric value "n" back to text.
             *
             * Currrently, the only supported format is "displayLeadingZero"
             * (i.e., if ".5" was input, format to "0.5"),
             * so calling String() is sufficient.
             *
             * Later on, we will likely implement other formatters,
             * to be applied here.
             *
             * 23-07-17 - doing this on commit only as this can
             * get in the way otherwise.
             */
            if (commit && format === "displayLeadingZero") {
                t = String(n)
            }

            setText([t, n])
            nextValue = n
        } else {
            setText([t, undefined])
        }

        // On commit, setValue is called only if the value has actually changed
        // This is to prevent unnecessary calls to setValue(), which would occur
        // simply by entering and leaving an input
        // Just out of precaution, this behaviour is not enabled if commitOnBlur
        // is true, because this may imply that the call site needs to receive
        // notifications on blur

        if (!commit || commitOnBlur || nextValue !== value) {
            setValue?.(nextValue)
        }
    }

    /**
     * This is called by the "onChange" handler.
     *
     * If "commitOnBlur" is true, this only changes the text in the state, and
     * leaves the current value unchanged.
     *
     * The call is to avoid having the initial "useEffect" handler triggered on
     * next render, because this would cancel the change on the text.
     *
     * Otherwise, it simply calls set() to change both the text and value.
     *
     * @param t Text
     */
    const setOnChange = (t: string) => {
        if (commitOnBlur) {
            setText([t, value])
        } else {
            set(t, false)
        }
    }

    /**
     * This is called by the "onBlur" handler
     *
     * If "commitOnBlur", or if a "format" parameter was passed,
     * then this calls "set" to update both text and value
     *
     * @param t Text
     */

    const setOnBlur = (t: string) => {
        if (commitOnBlur || format) {
            set(t, true)
        }
    }

    /**
     * This is called when clicking in the input, to select all its text
     */
    const onClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        ref.current.select()
        if (onClickAction) {
            onClickAction()
        }
    }

    /**
     * Prevent mouse wheel from changing the input value
     * This ensures scrolling only controls page navigation
     */
    const onWheel = (e: React.WheelEvent) => {
        // Prevent the default behavior (value change)
        ;(e.currentTarget as HTMLInputElement).blur()
    }

    const w = text.length + 3

    let style = cssStyle ? { ...cssStyle } : {}
    if (setWidthFromLength) {
        style.width = w + "ch"
    }

    return (
        <input
            type="number"
            ref={ref}
            readOnly={readOnly}
            disabled={disabled}
            placeholder={isUndefinedOrNull(defaultValue) ? placeholder : String(defaultValue)}
            className={classes(["form-control", spinners ? "" : "no-spinners", className])}
            style={style}
            value={text}
            min={min}
            max={max}
            required={required}
            step={step}
            onClick={onClick}
            onChange={(e) => setOnChange(e.target.value)}
            onBlur={(e) => setOnBlur(e.target.value)}
            onWheel={onWheel}
        ></input>
    )
}

export function FormGroupNumericInput(
    props: NumericInputProps & {
        label?: ReactNode
        labelStyle?: CSSProperties
        invalid?: boolean | string
        inputClassName?: string
    }
) {
    const { className, label, labelStyle, invalid, inputClassName, ...inputProps } = props
    return (
        <FormGroup
            label={label}
            labelStyle={labelStyle}
            className={className}
            required={props.required}
            invalid={invalid}
        >
            <NumericInput {...inputProps} className={inputClassName} />
        </FormGroup>
    )
}
